import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Mail, Lock, User, Building2, ArrowRight, Fingerprint } from 'lucide-react';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { Button } from '@/components/ui/button';
import { LogoFull } from '@/components/Logo';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { useAuth } from '@/contexts/AuthContext';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

const Auth = () => {
  const navigate = useNavigate();
  const { login, register, verify2FA, requires2FA, loginWithGoogle } = useAuth();
  
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totpCode, setTotpCode] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    company_name: ''
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password);
        if (result.success) navigate('/builder');
      } else {
        await register(formData);
        navigate('/builder');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerify = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await verify2FA(totpCode);
      if (result.success) navigate('/builder');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code');
      setTotpCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setError('');
    try {
      await loginWithGoogle(credentialResponse.credential);
      navigate('/builder');
    } catch (err) {
      setError('Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  // 2FA Verification Screen
  if (requires2FA) {
    return (
      <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md bg-white rounded-2xl border border-[#DCE4ED] p-8"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#00AEEF]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Fingerprint size={32} className="text-[#1A3A64]" />
            </div>
            <h1 className="text-2xl font-semibold text-[#0D1B2A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Two-Factor Authentication
            </h1>
            <p className="text-[#3D5A80] mt-2">Enter the code from your authenticator app</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-[#E74C3C]/10 border border-[#E74C3C]/20 rounded-lg text-[#E74C3C] text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-center mb-6">
            <InputOTP maxLength={6} value={totpCode} onChange={setTotpCode} data-testid="2fa-code-input">
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>

          <Button
            className="w-full btn-primary"
            size="lg"
            onClick={handle2FAVerify}
            disabled={loading || totpCode.length !== 6}
            data-testid="verify-2fa-btn"
          >
            {loading ? 'Verifying...' : 'Verify Code'}
          </Button>
        </motion.div>
      </div>
    );
  }

  const AuthForm = (
    <div className="min-h-screen bg-[#F0F4F8] flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full max-w-md"
        >
          <Link to="/" className="flex items-center gap-2 mb-10">
            <LogoFull size="default" />
          </Link>

          <h1 className="text-3xl font-semibold text-[#0D1B2A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {isLogin ? 'Welcome back' : 'Create your account'}
          </h1>
          <p className="text-[#3D5A80] mb-8">
            {isLogin ? 'Sign in to continue building your custom policy' : 'Start building your perfect insurance coverage'}
          </p>

          {/* Google OAuth */}
          {GOOGLE_CLIENT_ID && (
            <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
              <div className="mb-4">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => setError('Google sign-in failed')}
                  theme="outline"
                  size="large"
                  width="100%"
                  text={isLogin ? "signin_with" : "signup_with"}
                />
              </div>
            </GoogleOAuthProvider>
          )}

          {/* Social Login Buttons */}
          <div className="grid grid-cols-2 gap-2 mb-4">
            {[
              { provider: 'microsoft', label: 'Microsoft', color: '#00A4EF', icon: 'M' },
              { provider: 'linkedin', label: 'LinkedIn', color: '#0A66C2', icon: 'in' },
              { provider: 'apple', label: 'Apple', color: '#000000', icon: '' },
              { provider: 'facebook', label: 'Facebook', color: '#1877F2', icon: 'f' },
            ].map(({ provider, label, color, icon }) => (
              <button
                key={provider}
                type="button"
                onClick={() => {
                  setError(`${label} login requires API credentials. Contact admin to configure.`);
                }}
                className="flex items-center justify-center gap-2 py-2.5 rounded-lg border border-[#DCE4ED] text-sm font-medium text-[#3D5A80] hover:bg-[#F0F4F8] transition-colors"
                data-testid={`social-login-${provider}`}
              >
                <span className="w-5 h-5 rounded flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: color }}>
                  {icon}
                </span>
                {label}
              </button>
            ))}
          </div>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#DCE4ED]" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-[#F0F4F8] text-[#8DA0B9]">or continue with email</span>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-[#E74C3C]/10 border border-[#E74C3C]/20 rounded-lg text-[#E74C3C] text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <>
                <div>
                  <Label htmlFor="full_name" className="text-[#0D1B2A]">Full Name</Label>
                  <div className="relative mt-1.5">
                    <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8DA0B9]" />
                    <Input
                      id="full_name"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      className="pl-10 border-[#DCE4ED]"
                      placeholder="John Smith"
                      required={!isLogin}
                      data-testid="full-name-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="company_name" className="text-[#0D1B2A]">Company Name (optional)</Label>
                  <div className="relative mt-1.5">
                    <Building2 size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8DA0B9]" />
                    <Input
                      id="company_name"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleInputChange}
                      className="pl-10 border-[#DCE4ED]"
                      placeholder="Acme Inc."
                      data-testid="company-input"
                    />
                  </div>
                </div>
              </>
            )}

            <div>
              <Label htmlFor="email" className="text-[#0D1B2A]">Email</Label>
              <div className="relative mt-1.5">
                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8DA0B9]" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="pl-10 border-[#DCE4ED]"
                  placeholder="you@company.com"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-[#0D1B2A]">Password</Label>
              <div className="relative mt-1.5">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8DA0B9]" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="pl-10 border-[#DCE4ED]"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button type="submit" className="w-full btn-primary py-6" size="lg" disabled={loading} data-testid="submit-auth-btn">
              {loading ? 'Loading...' : (isLogin ? 'Sign In' : 'Create Account')}
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </form>

          <p className="text-center text-[#3D5A80] mt-6">
            {isLogin ? "Don't have an account?" : 'Already have an account?'}{' '}
            <button
              onClick={() => { setIsLogin(!isLogin); setError(''); }}
              className="text-[#1A3A64] font-medium hover:underline"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? 'Sign up' : 'Sign in'}
            </button>
          </p>

          {/* Legal Links */}
          <div className="mt-8 pt-6 border-t border-[#DCE4ED] text-center text-sm text-[#8DA0B9]">
            By continuing, you agree to our{' '}
            <Link to="/terms" className="text-[#1A3A64] hover:underline">Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-[#1A3A64] hover:underline">Privacy Policy</Link>
          </div>
        </motion.div>
      </div>

      {/* Right Panel - Visual */}
      <div className="hidden lg:flex flex-1 bg-[#1A3A64] items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#00AEEF] rounded-full blur-3xl" />
        </div>
        
        <div className="relative text-white max-w-lg">
          <h2 className="text-4xl font-semibold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Build insurance that fits your business
          </h2>
          <p className="text-white/70 text-lg mb-8">
            Our AI-powered builder helps you create custom coverage in minutes, not weeks.
          </p>
          
          <div className="space-y-4">
            {[
              'Visual drag-and-drop builder',
              'Real-time AI recommendations',
              'Industry-specific templates',
              'US-compliant coverage options',
              'Two-factor authentication'
            ].map((feature, index) => (
              <motion.div
                key={feature}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="flex items-center gap-3"
              >
                <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center">
                  <Shield size={12} className="text-white" />
                </div>
                <span className="text-white/90">{feature}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return AuthForm;
};

export default Auth;
